import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CvHealthReport,
  CvHealthReportEnvelope,
  CvHealthStage,
  StartCvHealthReviewInput,
} from "@tabbio/types";
import {
  CV_HEALTH_NOT_FOUND,
  getCvHealthReview,
  isCvHealthFeatureDisabled,
  startCvHealthReview,
} from "../lib/cv-health-api";
import { deriveStage } from "../lib/cv-health-model";

/**
 * Drives one CV Health review from a client: start it, poll while the AI
 * pass runs, and settle. Late responses from a superseded run are ignored,
 * so Stop/retry/unmount can never apply a stale result.
 *
 * `phase` is the request lifecycle; `stage` is what the status line shows;
 * `report` is the server's truth. When the feature is disabled server-side
 * the phase becomes `disabled` and callers skip the step (onboarding) or
 * hide the control (editor).
 */

export type CvHealthReviewPhase =
  | "idle"
  | "starting"
  | "running"
  | "settled"
  | "failed"
  | "disabled";

const POLL_INTERVAL_MS = 700;
/** Past this the client stops polling and shows the retryable failure. The server marks stale runs partial on its own. */
const POLL_TIMEOUT_MS = 45_000;
/**
 * How long polls may keep failing (no answer since the last good one) before
 * the run counts as failed: rides out a network switch or a short outage, but
 * an API that keeps erroring doesn't hold the checking screen for the whole
 * timeout. A missing report fails at once (nothing to wait for).
 */
const POLL_ERROR_BUDGET_MS = 15_000;

export interface UseCvHealthReviewOptions {
  /** Called with the settled envelope (complete, partial, unreadable). */
  onSettled?: (envelope: CvHealthReportEnvelope) => void;
  /** Called when the server says the feature is off. */
  onDisabled?: () => void;
}

export interface UseCvHealthReviewResult {
  phase: CvHealthReviewPhase;
  stage: CvHealthStage;
  report: CvHealthReport | null;
  envelope: CvHealthReportEnvelope | null;
  errorCode: string | null;
  /** Start a new review. A second call while one is in flight supersedes it. */
  start: (input: StartCvHealthReviewInput) => void;
  /** Adopt a report fetched elsewhere (editor "latest" endpoint) without a new review. */
  adopt: (envelope: CvHealthReportEnvelope) => void;
  /** Stop polling and forget the run. Never applies anything. */
  reset: () => void;
}

export function useCvHealthReview(
  options: UseCvHealthReviewOptions = {},
): UseCvHealthReviewResult {
  const [phase, setPhase] = useState<CvHealthReviewPhase>("idle");
  const [envelope, setEnvelope] = useState<CvHealthReportEnvelope | null>(
    null,
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const runIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    clearTimer();
    setPhase("idle");
    setEnvelope(null);
    setErrorCode(null);
  }, [clearTimer]);

  const settle = useCallback(
    (runId: number, next: CvHealthReportEnvelope) => {
      if (runId !== runIdRef.current) return;
      clearTimer();
      setEnvelope(next);
      if (next.report.status === "failed") {
        setErrorCode(next.report.errorCode ?? "REVIEW_FAILED");
        setPhase("failed");
        return;
      }
      setPhase("settled");
      optionsRef.current.onSettled?.(next);
    },
    [clearTimer],
  );

  const fail = useCallback(
    (runId: number, code: string) => {
      if (runId !== runIdRef.current) return;
      clearTimer();
      setErrorCode(code);
      setPhase("failed");
    },
    [clearTimer],
  );

  const poll = useCallback(
    (runId: number, reportId: string, startedAt: number, lastOkAt = startedAt) => {
      clearTimer();
      timerRef.current = setTimeout(async () => {
        if (runId !== runIdRef.current) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          fail(runId, "REVIEW_TIMEOUT");
          return;
        }
        const response = await getCvHealthReview(reportId);
        if (runId !== runIdRef.current) return;
        if (response.error || !response.data) {
          // A transient poll error keeps polling; the timeout and the error
          // budget bound it.
          const code = response.error?.code ?? "REVIEW_POLL_FAILED";
          if (
            code === CV_HEALTH_NOT_FOUND ||
            Date.now() - lastOkAt >= POLL_ERROR_BUDGET_MS
          ) {
            fail(runId, code);
            return;
          }
          poll(runId, reportId, startedAt, lastOkAt);
          return;
        }
        setEnvelope(response.data);
        if (response.data.report.status === "running") {
          poll(runId, reportId, startedAt, Date.now());
          return;
        }
        settle(runId, response.data);
      }, POLL_INTERVAL_MS);
    },
    [clearTimer, fail, settle],
  );

  const start = useCallback(
    (input: StartCvHealthReviewInput) => {
      runIdRef.current += 1;
      const runId = runIdRef.current;
      clearTimer();
      setErrorCode(null);
      setEnvelope(null);
      setPhase("starting");
      const startedAt = Date.now();
      void startCvHealthReview(input).then((response) => {
        if (runId !== runIdRef.current) return;
        if (isCvHealthFeatureDisabled(response)) {
          setPhase("disabled");
          optionsRef.current.onDisabled?.();
          return;
        }
        if (response.error || !response.data) {
          fail(runId, response.error?.code ?? "REVIEW_START_FAILED");
          return;
        }
        setEnvelope(response.data);
        if (response.data.report.status === "running") {
          setPhase("running");
          poll(runId, response.data.report.id, startedAt);
          return;
        }
        settle(runId, response.data);
      });
    },
    [clearTimer, fail, poll, settle],
  );

  const adopt = useCallback(
    (next: CvHealthReportEnvelope) => {
      runIdRef.current += 1;
      const runId = runIdRef.current;
      clearTimer();
      setErrorCode(null);
      setEnvelope(next);
      if (next.report.status === "running") {
        setPhase("running");
        poll(runId, next.report.id, Date.now());
        return;
      }
      settle(runId, next);
    },
    [clearTimer, poll, settle],
  );

  useEffect(() => {
    return () => {
      runIdRef.current += 1;
      clearTimer();
    };
  }, [clearTimer]);

  const report = envelope?.report ?? null;
  const stage = deriveStage(
    phase === "disabled" ? "idle" : phase,
    report,
  );

  return { phase, stage, report, envelope, errorCode, start, adopt, reset };
}
